import Backbone from 'backbone'
import _ from 'lodash'
import moment from 'moment'
import momentHelper from 'scoreboard/components/moment-helper'
import DateInput from 'scoreboard/components/date-input'
import MarkedField from 'scoreboard/components/marked-field'
import Sorter from 'scoreboard/components/sorter'
import { nilCompare as nilCompareInstance } from 'scoreboard/components/sorter'
const nilCompare = nilCompareInstance(true)
import numericEvaluator from 'scoreboard/components/evaluators/numeric-evaluator'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import nonNegativeNumberValidator from 'scoreboard/components/validators/non-negative-number-validator'
import template from './weight-date-table.hbs'
import rowTemplate from './weight-date-row.hbs'

function markNumericField(el) {
	return new MarkedField({
		el: el,
		evaluator: numericEvaluator,
		validator: [requiredValidator, nonNegativeNumberValidator]
	}).render()
}

const WeightDateTable = Backbone.View.extend({

	className: 'weight-date-table',

	events: {
		'keyup input[name=weight]': '_updateWeight',
		'click .delete': '_onClickDelete',
		'click .add' : '_onClickAdd'
	},

	initialize: function(options = {}) {
		this.opts = options
		this.weightNode = options.weightNode || {periods: []}
		this.formView = this.opts.formView

		let periods = this.weightNode.periods
		if (_.isEmpty(periods)) {
			periods.push({weight:1})	//ensure a default
		}
		this._dates = _.map(periods, period => {
			let start = _.isNil(period.start) ? null :  moment(period.start).startOf('date')
			let end = _.isNil(period.end) ? null :  moment(period.end).startOf('date')
			return _.defaults({start, end}, period)
		})

		this.sorter = new Sorter({
			nilsFirst: true,
			compare: (a,b) => {
				let v = nilCompare(a.start,b.start)
				if (v == null) {
					if (a.start.isSame(b.start, 'date')) {
						v = 0
					} else {
						v = a.start.isBefore(b.start, 'date') ? -1 : 1
					}
				}
				return v
			}
		})
	},

	render: function() {
		this.$el.html(template(this.weightNode))
		this._recalculateRanges()
		return this
	},

	_onClickAdd: function(e) {
		let dateToUse = moment().startOf('date')
		if (this._dates.length > 1) {
			//#29492 ensure adding is always appended to the last date or today
			//depending on the start date of the last range
			let lastDate = _.last(this._dates).start
			if (lastDate.isAfter(dateToUse)) {
				dateToUse = lastDate.clone()
				dateToUse.add(1, 'days')
			}
		}
		this._dates.push({start:dateToUse, end:dateToUse.clone(), weight: 1, nodeId: this.weightNode.id})
		this._recalculateRanges()
	},

	/**
	 * Parses all the time ranges in the form and ensures the ranges are ordered
	 * in a single continuous unbroken time range from earliest to latest.  The
	 * first range will always have a NULL start date while the last range will
	 * always have a NULL end date.  This method also guarantees no gaps in
	 * between dates nor will any range overlap with another.
	 * @private
	 */
	_recalculateRanges: function() {
		//first sort the dates by start date
		this._dates = this.sorter.sort(this._dates).results

		//then ensure there is no overlap or gaps
		for (let i=1; i<this._dates.length; i++) {
			let startDate = this._dates[i].start
			let endDate = this._dates[i].end
			let prevStartDate = this._dates[i-1].start

			if (startDate.isSame(prevStartDate)) {
				startDate.add(1, 'days')
				if (!endDate || endDate.isBefore(startDate)) {
					this._dates[i].end = startDate.clone()
				}
			}
			this._dates[i-1].end = startDate.clone().subtract(1, 'days')
			if (i == this._dates.length-1) {
				this._dates[i].end = null
			}
		}

		this._renderInputs()
	},

	_renderInputs: function() {
		this._initInputs()
		this.$('tbody').html(rowTemplate({dates: this._dates}))
		_.invoke(this.formView, 'delegateEvents')
		_.each(this.$('input[name=start]'), (el, index) => {
			let dateInput = new DateInput({
				el,
				initialValue: this._dates[index+1].start,
				required: true
			}).render()
			this.listenTo(dateInput, 'calendar-hide', () =>
				this._updateDate(dateInput, index+1))
			this._startDateInputs.push(dateInput)
		})
		_.each(this.$('input[name=weight]'), el => {
			this._weightInputs.push(markNumericField(el))
		})
		//cannot remove the only weight
		let singleWeight = this._dates.length < 2
		if (singleWeight) {
			this.$('.delete').remove(singleWeight)
		}
		this.$('.delete-header, .delete-cell')
			.toggleClass('is-not-displayed', singleWeight)
		this.$('.add-row').prop('colspan', singleWeight ? 3 : 4)
	},

	_updateDate: function(dateInput, dateInputIndex) {
		this._dates[dateInputIndex].start = dateInput.getMoment().clone().startOf('date')
		this._recalculateRanges()
		_.invokeMap(this._weightInputs, 'validate')
	},

	_updateWeight: function(e) {
		e.preventDefault()
		let weight = this.$(e.target)
		let index = weight.closest('tr').data('index')
		this._dates[index].weight = weight.val()
	},

	_onClickDelete: function(e) {
		e.preventDefault()
		if (this._dates.length < 2) { return }

		let index = this.$(e.target).closest('tr').data('index')
		this._dates.splice(index, 1)
		//ensure the begin/end of time is set
		this._dates[0].start = null
		this._dates[this._dates.length-1].end = null
		this._recalculateRanges()
	},

	_initInputs: function() {
		_.invokeMap(this._startDateInputs, 'remove')
		this._startDateInputs = []
		_.invokeMap(this._weightInputs, 'remove')
		this._weightInputs = []
	},

	/**
	 * Returns an array of objects contains the begin/end dates and the corresponding
	 * weight for that date range in the table
	 */
	gather: function() {
		return _.map(this._dates, date => {
			return _.defaults({
				start: momentHelper.isoDate(date.start),
				end: momentHelper.isoDate(date.end)
			}, date)
		})
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		this._initInputs()
	}

})

export default WeightDateTable

WeightDateTable.__filename__ = 'scorecards/edit/weight-date-table.js'
